<template>
  <div class="modificator-requirement" :class="`--mode-${mode}`">
    <!-- КОМПАКТНЫЙ РЕЖИМ (для ProductCardLine, ProductCardSlide) -->
    <template v-if="mode === 'compact'">
      <!-- Текстовое описание требования -->
      <div
        v-if="requirementText"
        class="requirement-text s-font-2xs text-grey-7"
      >
        {{ requirementText }}
      </div>

      <!-- Прогресс бар для глобальных модификаторов с ограничением по сумме -->
      <div v-if="showProgress" class="requirement-progress q-mt-xs">
        <div class="progress-bar-container">
          <div class="progress-bar-bg">
            <div
              class="progress-bar-fill"
              :class="progressColorClass"
              :style="{ width: `${progressPercent}%` }"
            />
          </div>
        </div>
        <div class="progress-text s-font-3xs text-grey-7 q-mt-xs">
          При заказе от <span class="text-no-wrap">{{ formatPrice(minOrderPrice) }} ₽</span>
        </div>
      </div>
    </template>

    <!-- ПОДРОБНЫЙ РЕЖИМ (для ProductCardGrid) -->
    <template v-else-if="mode === 'detailed'">
      <div
        class="detailed-requirement q-mb-md q-pa-sm bg-grey-2 rounded-borders"
      >
        <div class="text-caption text-grey-8 q-mb-xs">
          Доступно с модификаторами:
        </div>

        <!-- Обычные модификаторы -->
        <div
          v-for="modOpt in regularModificatorOptions"
          :key="modOpt.modificator.ID"
          class="text-caption text-weight-medium"
          :class="modOpt.isAvailable ? 'text-positive' : 'text-grey-7'"
        >
          <q-icon
            :name="modOpt.isAvailable ? ICONS.checkCircle : ICONS.radioButtonUnchecked"
            size="14px"
            class="q-mr-xs"
          />
          {{ modOpt.modificator.NAME }}
        </div>

        <!-- Глобальные модификаторы -->
        <div
          v-for="globalOpt in globalModificatorOptions"
          :key="globalOpt.modificator.ID"
          class="modificator-item"
        >
          <!-- Если модификатор активен -->
          <div
            v-if="globalOpt.isActive"
            class="text-caption text-weight-medium text-positive"
          >
            <q-icon :name="ICONS.checkCircle" size="14px" class="q-mr-xs" />
            {{ globalOpt.modificator.NAME }}
            <span v-if="globalOpt.modificator.ORDER_PRICE_FROM">
              (от {{ formatPrice(globalOpt.modificator.ORDER_PRICE_FROM) }} ₽)
            </span>
          </div>

          <!-- Если модификатор неактивен - показываем прогресс -->
          <div v-else class="q-mt-xs">
            <div class="text-caption text-weight-medium text-grey-7 q-mb-xs">
              <q-icon
                :name="ICONS.radioButtonUnchecked"
                size="14px"
                class="q-mr-xs"
              />
              {{ globalOpt.modificator.NAME }}
            </div>
            <div class="progress-bar-container">
              <div class="progress-bar-bg">
                <div
                  class="progress-bar-fill"
                  :class="getProgressColorClass(globalOpt.progressPercent)"
                  :style="{ width: `${globalOpt.progressPercent}%` }"
                />
              </div>
            </div>
            <div class="progress-text s-font-3xs text-grey-7 q-mt-xs">
              При заказе от
              {{
                formatPrice(globalOpt.modificator.ORDER_PRICE_FROM ?? undefined)
              }}
              ₽
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import {ICONS} from "@/assets/icons";
import {matRadioButtonUnchecked} from "@quasar/extras/material-icons";

export interface ModificatorOptionData {
  modificator: {
    ID: number;
    NAME: string;
    ORDER_PRICE_FROM?: number | null;
  };
  isAvailable: boolean;
}

export interface GlobalModificatorOptionData {
  modificator: {
    ID: number;
    NAME: string;
    ORDER_PRICE_FROM?: number | null;
  };
  isActive: boolean; // Активен ли модификатор (достигнута минимальная сумма)
  progressPercent?: number;
  priceRemaining?: number;
}

const props = withDefaults(
  defineProps<{
    mode?: "compact" | "detailed"; // Режим отображения
    // Для компактного режима
    modificatorName?: string;
    isGlobal?: boolean;
    priceRemaining?: number;
    progressPercent?: number;
    minOrderPrice?: number;
    isAvailable?: boolean;
    // Для подробного режима
    regularModificatorOptions?: ModificatorOptionData[];
    globalModificatorOptions?: GlobalModificatorOptionData[];
  }>(),
  {
    mode: "compact",
    isAvailable: true,
    regularModificatorOptions: () => [],
    globalModificatorOptions: () => [],
  }
);

const showProgress = computed(() => {
  return (
    props.isGlobal &&
    props.priceRemaining !== undefined &&
    props.progressPercent !== undefined &&
    !props.isAvailable
  );
});

const requirementText = computed(() => {
  if (props.isAvailable) {
    return null;
  }

  if (props.isGlobal && props.priceRemaining !== undefined) {
    // Для глобальных модификаторов текст будет в progress-text
    return null;
  }

  // Для обычных модификаторов
  if (props.modificatorName) {
    return `Требуется: ${props.modificatorName}`;
  }

  return null;
});

const formatPrice = (price: number | undefined): string => {
  if (price === undefined) return "0";
  return Math.ceil(price).toString();
};

const minOrderPrice = computed(() => {
  if (props.priceRemaining !== undefined && props.priceRemaining > 0) {
    // Рассчитываем минимальную сумму: текущая корзина + недостающая сумма
    // Но для отображения используем priceRemaining + текущий basketTotal
    // Проще взять из modificator.ORDER_PRICE_FROM если есть
    return props.minOrderPrice;
  }
  return props.minOrderPrice;
});

// Определяем цвет прогресс бара в зависимости от процента (для компактного режима)
const progressColorClass = computed(() => {
  return getProgressColorClass(props.progressPercent || 0);
});

// Функция для определения цвета прогресс бара (используется в обоих режимах)
const getProgressColorClass = (percent: number | undefined): string => {
  const p = percent || 0;

  if (p < 40) {
    return "progress-low"; // Серый (0-40%)
  } else if (p < 80) {
    return "progress-medium"; // Желтый (40-80%)
  } else {
    return "progress-high"; // Зеленый (80-100%)
  }
};
</script>

<style scoped lang="scss">
.modificator-requirement {
  margin-top: 3px;
}

.requirement-text {
  line-height: 1.2;
}

.progress-bar-container {
  width: 100%;
}

.progress-bar-bg {
  height: 4px;
  background-color: #e0e0e0;
  border-radius: 2px;
  overflow: hidden;
  position: relative;
}

.progress-bar-fill {
  height: 100%;
  transition: width 0.3s ease, background 0.3s ease;

  // Серый (0-40%)
  &.progress-low {
    background: linear-gradient(90deg, #9e9e9e 0%, #bdbdbd 100%);
  }

  // Желтый (40-80%)
  &.progress-medium {
    background: linear-gradient(90deg, #ffa726 0%, #ffb74d 100%);
  }

  // Зеленый (80-100%)
  &.progress-high {
    background: linear-gradient(90deg, #66bb6a 0%, #81c784 100%);
  }
}

.progress-text {
  line-height: 1.1;
  text-align: left;
}

// Стили для подробного режима
.--mode-detailed {
  .detailed-requirement {
    .modificator-item {
      &:not(:last-child) {
        margin-bottom: 8px;
      }
    }
  }
}
</style>
